/**
 * Recently opened files (§10).
 *
 * §10's first user journey is "open a file you were sent". The second is
 * "open it again" — and without this the app sent people back to the messaging
 * application every single time, which is the friction the product exists to
 * remove.
 *
 * Three rules shape what is recorded and what is offered.
 *
 * **A file whose contents may be credentials is never recorded.** §29 excludes
 * sensitive files from recent lists, thumbnails and error reports, and the
 * detection layer has already decided which those are. Recording the *name* of
 * a `.env` is enough to leak which server it belongs to.
 *
 * **A handle is only offered when the host can actually reopen it.** On the
 * desktop and on a phone a handle is a real path. In a browser it is a
 * reference to a `File` the page was handed, which cannot outlive the tab — so
 * the browser records nothing rather than showing a list that fails on click.
 * §19: an affordance that cannot be honoured should not exist.
 *
 * **The entry is verified before it is shown.** A path that has been moved or
 * deleted must not appear as if it would open.
 */
import type { FileStore } from '@moduviewer/core';

const KEY = 'modu.recent.v1';

/** Enough to be useful, few enough that the list stays scannable. */
const MAX_ENTRIES = 12;

export interface RecentFile {
  readonly handle: string;
  readonly displayName: string;
  readonly formatId: string | null;
  readonly sizeBytes: number;
  readonly openedAtMs: number;
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    // Reading the property itself throws in some privacy modes.
    return null;
  }
}

function isRecent(value: unknown): value is RecentFile {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v['handle'] === 'string'
    && typeof v['displayName'] === 'string'
    && (typeof v['formatId'] === 'string' || v['formatId'] === null)
    && typeof v['sizeBytes'] === 'number'
    && typeof v['openedAtMs'] === 'number';
}

function readAll(): RecentFile[] {
  const store = storage();
  if (!store) return [];
  try {
    const parsed: unknown = JSON.parse(store.getItem(KEY) ?? '[]');
    // A half-written entry is dropped, not repaired: a repaired one would
    // offer to open a path that was never fully recorded.
    return Array.isArray(parsed) ? parsed.filter(isRecent) : [];
  } catch {
    return [];
  }
}

function writeAll(entries: readonly RecentFile[]): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)));
  } catch { /* a full quota is not worth interrupting an open for */ }
}

export interface RememberOptions {
  readonly handle: string;
  readonly displayName: string;
  readonly formatId: string | null;
  readonly sizeBytes: number;
  /** §29 — set by detection when the file may hold credentials. */
  readonly sensitive: boolean;
  /** False in a browser, where a handle cannot outlive the tab. */
  readonly reopenable: boolean;
}

/**
 * Record an opened file, unless recording it would be wrong.
 *
 * Returns whether anything was written, so a caller can tell "excluded" from
 * "storage unavailable" if it ever needs to.
 */
export function rememberRecentFile(options: RememberOptions, nowMs: number): boolean {
  if (options.sensitive || !options.reopenable) return false;

  const entry: RecentFile = {
    handle: options.handle,
    displayName: options.displayName,
    formatId: options.formatId,
    sizeBytes: options.sizeBytes,
    openedAtMs: nowMs,
  };
  // Reopening a file moves it to the top rather than adding a second row.
  writeAll([entry, ...readAll().filter((r) => r.handle !== entry.handle)]);
  return true;
}

/** Every entry, most recent first. Not yet checked against the filesystem. */
export function recentFiles(): RecentFile[] {
  return readAll().sort((a, b) => b.openedAtMs - a.openedAtMs);
}

export function forgetRecentFile(handle: string): void {
  writeAll(readAll().filter((r) => r.handle !== handle));
}

export function clearRecentFiles(): void {
  writeAll([]);
}

/**
 * Drop entries whose file is gone.
 *
 * A recent list that offers a file which has since been deleted is worse than
 * a short one: the click fails, and the failure looks like the app's fault.
 * Checked against the store rather than remembered, because the file can be
 * moved by anything at any time.
 */
export async function pruneMissing(store: FileStore): Promise<RecentFile[]> {
  const entries = recentFiles();
  const alive: RecentFile[] = [];

  for (const entry of entries) {
    try {
      const stat = await store.stat(entry.handle);
      if (stat.exists) alive.push(entry);
    } catch {
      // A handle the store cannot even stat is not one it can open.
    }
  }

  if (alive.length !== entries.length) writeAll(alive);
  return alive;
}
